/**
 * The top of an affiliate's page: who they are, how we work with them
 * (relationship + channel), status, owner, their cut, and hide / unhide.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Eye, EyeOff, Mail, Trash, UserRound } from "lucide-react";
import { Avatar, Button, Select, useToast } from "@/components/ui";
import { Card, timeAgo } from "@/features/command/ui";
import { InlineEditField } from "@/features/contacts/InlineEdit";
import {
  CHANNELS, CHANNEL_COLOR, CHANNEL_FALLBACK, KINDS, KIND_META, PillSelect, STATUSES, STATUS_META, kindBlurb, kindLabel, memberName, useTeam,
} from "./shared";
import type { AffiliateChannel, AffiliateDetail, AffiliateKind, AffiliateStatus, Rhythm } from "./types";

export type AffiliatePatch = Partial<{
  name: string; email: string | null; kind: AffiliateKind; channel: AffiliateChannel; channel_note: string | null;
  status: AffiliateStatus; owner_id: string | null; rate_pct: number | null; hidden: boolean;
  handles: Array<{ platform?: string | null; handle: string; url?: string | null }>; audience: number | null; notes: string | null;
}>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      {children}
    </div>
  );
}

export function DetailHeader({
  a, rhythm, onSave, onDelete,
}: {
  a: AffiliateDetail["affiliate"];
  rhythm: Rhythm | null;
  onSave: (patch: AffiliatePatch) => Promise<void>;
  /** Only for people added by hand; program people can only be hidden. */
  onDelete?: () => void;
}) {
  const toast = useToast();
  const team = useTeam();
  const { pathname } = useLocation();
  const meta = KIND_META[a.kind] ?? KIND_META.casual;
  const KindIcon = meta.icon;
  const contactName = [a.first_name, a.last_name].filter(Boolean).join(" ").trim();
  const suggestName = contactName && contactName.toLowerCase() !== a.name.trim().toLowerCase() && contactName.length > a.name.trim().length;
  const rate = a.rate_pct != null && a.rate_pct !== "" ? Number(a.rate_pct) : null;
  const ownerKnown = !a.owner_id || team.some((m) => m.id === a.owner_id);
  // Dropdowns and buttons: the page already toasts a failed save.
  const quiet = (patch: AffiliatePatch) => { onSave(patch).catch(() => {}); };

  return (
    <Card className="p-4 sm:p-5">
      {a.hidden && (
        <div className="-mx-4 -mt-4 mb-4 flex flex-col gap-2 rounded-t-2xl border-b border-zinc-800/70 bg-zinc-800/30 px-4 py-2.5 text-[13px] text-zinc-400 sm:-mx-5 sm:-mt-5 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <span className="flex items-center gap-2"><EyeOff className="h-3.5 w-3.5" /> Hidden from the list and the totals. No follow-ups are planned.</span>
          <Button size="sm" variant="secondary" leftIcon={<Eye className="h-3.5 w-3.5" />} onClick={() => quiet({ hidden: false })}>Unhide</Button>
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <Avatar src={a.avatar_url} name={a.name} email={a.email} size="xl" />
          <div className="min-w-0 flex-1">
            <InlineEditField
              value={a.name}
              label="Name"
              formatDisplay={(v) => <span className="text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">{v}</span>}
              onSave={async (v) => {
                if (!v) { toast.error("They need a name"); throw new Error("empty"); }
                await onSave({ name: v });
              }}
            />
            {suggestName && (
              <button
                type="button"
                onClick={() => quiet({ name: contactName })}
                className="mt-0.5 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
              >
                Use their full name: {contactName}
              </button>
            )}
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
              <div className="min-w-0 flex-1">
                <InlineEditField
                  value={a.email}
                  kind="email"
                  label="Email"
                  placeholder="Add their email"
                  onSave={async (v) => {
                    if (v && !EMAIL.test(v)) { toast.error("That email doesn't look right"); throw new Error("bad email"); }
                    await onSave({ email: v });
                  }}
                />
              </div>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {a.contact_id ? (
                <Link to={`/contacts/${a.contact_id}`} state={{ from: pathname }} className="inline-flex items-center gap-1 font-medium text-indigo-300 hover:text-indigo-200">
                  <UserRound className="h-3.5 w-3.5" /> Open their contact
                </Link>
              ) : (
                <span className="text-zinc-500">No contact linked yet</span>
              )}
              <span className="text-zinc-500">{a.last_contact_at ? `Last in touch ${timeAgo(a.last_contact_at)}` : "No touch logged yet"}</span>
            </div>
          </div>
        </div>
        {(!a.hidden || onDelete) && (
          <div className="flex shrink-0 items-center gap-1 self-start">
            {!a.hidden && (
              <Button size="sm" variant="ghost" leftIcon={<EyeOff className="h-3.5 w-3.5" />} onClick={() => quiet({ hidden: true })}>
                Hide
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="ghost" className="text-rose-300 hover:text-rose-200" leftIcon={<Trash className="h-3.5 w-3.5" />} onClick={onDelete}>
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-zinc-800/60 pt-4 sm:grid-cols-3 lg:grid-cols-5">
        <Field label="Relationship">
          <PillSelect<AffiliateKind>
            value={a.kind}
            color={meta.color}
            title="Relationship"
            options={KINDS.map((k) => ({ value: k, label: kindLabel(k) }))}
            onChange={(v) => quiet({ kind: v })}
          />
        </Field>
        <Field label="Channel">
          <PillSelect<AffiliateChannel>
            value={a.channel}
            color={CHANNEL_COLOR[a.channel] ?? "zinc"}
            title="Where they sell us"
            options={CHANNELS.map((c) => ({ value: c, label: CHANNEL_FALLBACK[c] }))}
            onChange={(v) => quiet({ channel: v })}
          />
          {a.channel !== "syncsuite" && (
            <InlineEditField
              value={a.channel_note}
              label="Where exactly"
              placeholder={a.channel === "clickbank" ? "Their ClickBank name" : "Where exactly?"}
              className="mt-1 text-xs"
              onSave={async (v) => { await onSave({ channel_note: v }); }}
            />
          )}
        </Field>
        <Field label="Status">
          <PillSelect<AffiliateStatus>
            value={a.status}
            color={STATUS_META[a.status]?.color ?? "zinc"}
            title="Status"
            options={STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))}
            onChange={(v) => quiet({ status: v })}
          />
        </Field>
        <Field label="Looked after by">
          <Select
            value={a.owner_id ?? ""}
            onChange={(e) => quiet({ owner_id: e.target.value || null })}
            className="h-7 rounded-md text-[13px]"
            aria-label="Owner"
          >
            <option value="">Nobody</option>
            {team.map((m) => <option key={m.id} value={m.id}>{memberName(m.name)}</option>)}
            {!ownerKnown && a.owner_id && <option value={a.owner_id}>{memberName(a.owner_name) || "Someone else"}</option>}
          </Select>
        </Field>
        <Field label="Their cut">
          <InlineEditField
            value={rate != null ? String(rate) : ""}
            kind="number"
            label="Their cut"
            placeholder="Set a %"
            formatDisplay={(v) => `${Number(v)}% for life`}
            onSave={async (v) => {
              const n = v == null ? null : Number(v);
              if (n != null && (!Number.isFinite(n) || n < 0 || n > 100)) { toast.error("Pick a number from 0 to 100"); throw new Error("bad rate"); }
              await onSave({ rate_pct: n });
            }}
          />
        </Field>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-zinc-900/50 px-3 py-2.5 text-xs text-zinc-400 ring-1 ring-inset ring-zinc-800/60">
        <KindIcon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.iconClass}`} />
        <span><span className="font-medium text-zinc-200">{kindLabel(a.kind)}.</span> {a.kind === "partner" ? kindBlurb(a.kind) : rhythm?.blurb ?? kindBlurb(a.kind)}</span>
      </div>
    </Card>
  );
}
